import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { EmptyState, LoadingState } from '@/components/EmptyState';
import { useLanguage } from '@/i18n/LanguageContext';
import type { ChannelCard as Card } from '@/api/types';
import { cardKey } from '@/lib/channels';
import { ChannelCard } from './ChannelCard';
import { OnboardingNotice } from '@/components/OnboardingNotice';

/**
 * Seller Portal -> Integrations: one card per marketplace (Wildberries, Ozon,
 * Yandex Market) for each company the seller works for. The list comes from
 * the server; after any action the server's fresh card replaces the old one
 * in place (no full reload, so a result message stays visible). A load error
 * is shown in plain words - no codes, no ids.
 */
export function IntegrationsPage(): JSX.Element {
  const { t } = useLanguage();
  const result = useApi(() => endpoints.channels(), []);
  const [patched, setPatched] = useState<Record<string, Card>>({});

  function reload(): void {
    setPatched({});
    result.reload();
  }

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') {
    return (
      <div>
        <div className="page-header"><h1>{t('integrations.title')}</h1></div>
        <div className="error-banner" role="alert">
          <div>{t('integrations.loadError')}</div>
          <button type="button" onClick={reload} style={{ marginTop: 8 }}>{t('common.retry')}</button>
        </div>
      </div>
    );
  }

  const owners = result.data.owners;
  if (owners.length === 0) {
    return (
      <div>
        <div className="page-header"><h1>{t('integrations.title')}</h1></div>
        <EmptyState title={t('integrations.noOwnersTitle')} hint={t('integrations.noOwnersHint')} />
      </div>
    );
  }
  const multi = owners.length > 1;

  return (
    <div className="integrations">
      <div className="page-header">
        <h1>{t('integrations.title')}</h1>
        <span className="sub">{t('integrations.subtitle')}</span>
      </div>
      {owners.map((owner) => (
        <section key={owner.id} className="integrations-owner" aria-label={multi ? owner.name : t('integrations.title')}>
          {multi && <h2 className="section-title">{owner.name}</h2>}
          {owner.warehouses.length === 0 && (
            // The marketplace needs the contract (organisation policy) -> the contract wording; otherwise the plain onboarding reason.
            owner.contract?.required !== false && owner.onboarding?.reason !== 'NO_WAREHOUSE'
              ? <div className="notice" role="status" data-testid="integrations-contract-notice">{t('integrations.noWarehouse')}</div>
              : <OnboardingNotice reason={owner.onboarding?.reason === 'READY' ? 'NO_WAREHOUSE' : owner.onboarding?.reason ?? 'NO_WAREHOUSE'} />
          )}
          <p className="meta integrations-storage">{t('integrations.note.storage')}</p>
          <div className="channel-grid">
            {owner.channels.map((card) => (
              <ChannelCard
                key={card.kind}
                owner={owner}
                card={patched[cardKey(owner.id, card.kind)] ?? card}
                onCard={(next) => setPatched((p) => ({ ...p, [cardKey(owner.id, card.kind)]: next }))}
                onStale={reload}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
